import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, ArrowLeft, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      <div className="ambient-bg" />
      <div className="hidden md:flex flex-1 relative border-r border-zinc-900 p-12 flex-col justify-between">
        <BrandLogo variant="lockup" iconSize={36} to="/" testid="forgot-brand" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-4">
            reset access
          </div>
          <h2 className="text-4xl font-bold tracking-tight text-zinc-50 leading-tight">
            No lockouts.
            <br />
            No lost preparation.
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            We&apos;ll send a secure link to your inbox. Follow it to set a new password. Your saved
            resumes, questions, and practice history stay right where you left them.
          </p>
        </div>
        <div className="text-xs text-zinc-600 tracking-widest uppercase">© 2026 Intuiterview</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-in">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Reset your password</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Enter your email. If an account exists, we&apos;ll send a reset link.
            </p>
          </div>

          {sent ? (
            <div
              data-testid="forgot-success"
              className="p-5 rounded-xl border border-emerald-500/25 bg-emerald-500/5 text-sm text-emerald-200 leading-relaxed"
            >
              <Check className="h-4 w-4 inline mr-2 -mt-0.5" />
              If an account with <span className="font-medium">{email}</span> exists, a password
              reset link is on its way. Check your inbox. The link expires in 1 hour.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              {error && (
                <div
                  data-testid="forgot-error"
                  className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400 disabled:opacity-60 font-semibold rounded-lg px-4 py-2.5 transition-colors"
              >
                {submitting ? "Sending…" : "Send reset link"}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          )}

          <div className="mt-6 text-sm text-zinc-500">
            <Link
              to="/login"
              data-testid="forgot-back-link"
              className="inline-flex items-center gap-1 text-zinc-300 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
